import { useState } from "react";
import { Translate, useTranslation } from "@privacy/translations";
import { Box, Stack } from "@mui/material";
import { DynamicForm } from "../../dynamicForm";
import {
  dpiaByIdQueryOptions,
  dpiaFormQueryOptions,
  dpiaGridViewQueryOptions,
  dpiaHistoryQueryOptions,
  deleteDpiaMutationOptions,
  exportDpiaPdfMutationOptions,
} from "../../api/dpia";
import { useUserInfo } from "../../api/userInfo";
import type { DpiaById } from "../../api/dpia.types";
import type { TranslationKey } from "@privacy/translations";
import { Drawer } from "../../shared/Drawer";
import { DownloadIcon, DpiaIcon2, PencilIcon, TriangleAlertIcon } from "../../shared/icons";
import { usePrivacyNavigate } from "../../shared/usePrivacyNavigate";
import { downloadBlob } from "../../shared/downloadBlob";
import { HACK_alignIconWithLabelBox } from "@privacy/ui";
import { DpiaEditDialog } from "./DpiaEditDialog";
import { ActionMenu, ActionMenuDeleteMenuItem, ActionMenuItemWithPermission } from "../../shared/ActionMenu";
import { useMutation, useQuery } from "@tanstack/react-query";
import { prefetch } from "../../api/prefetch";

export const useDpiaFormTabPath = (dpiaId: string) => {
  const { paths } = usePrivacyNavigate();
  const formQuery = useQuery({ ...dpiaFormQueryOptions(), enabled: false });
  const firstSectionId = formQuery.data?.sections[0]?.id;
  return firstSectionId ? `${paths.dpiaForm(dpiaId)}/${firstSectionId}` : paths.dpiaForm(dpiaId);
};

type DpiaDrawerHeaderProps = { dpia: DpiaById; activeTab: "preAssessment" | "form" };

export const DpiaDrawerHeader = ({ dpia, activeTab }: DpiaDrawerHeaderProps) => {
  const { t } = useTranslation();
  const { navigate, paths } = usePrivacyNavigate();
  const formState = DynamicForm.useFormState();
  const formTabPath = useDpiaFormTabPath(dpia.id);
  const handleClose = () => navigate(paths.dpiaGrid());

  return (
    <>
      <Drawer.Header
        status={DynamicForm.formStateToDrawerHeaderStatus(formState)}
        onClose={handleClose}
        title={dpia.name}
        renderIcon={props => <DpiaIcon2 {...props} />}
        bottomSlot={
          <Stack sx={{ px: Drawer.PADDING_X }}>
            <Drawer.TabButtons>
              <Drawer.TabButton active={false} to={paths.dpiaById(dpia.id)}>
                {t("Dynamic.Drawer.OverviewButton")}
              </Drawer.TabButton>
              <Drawer.TabButton active={activeTab === "preAssessment"} to={paths.dpiaPreAssessmentById(dpia.id)}>
                {t("DpiaPreAssessment.PreAssessment")}
              </Drawer.TabButton>
              {dpia.preAssessment.isCompleted && (
                <Drawer.TabButton
                  active={activeTab === "form"}
                  to={formTabPath}
                  onHover={() => prefetch(dpiaFormQueryOptions())}
                >
                  {t("RegistrationDrawer.Dpia")}
                </Drawer.TabButton>
              )}
              <Drawer.TabButton
                active={false}
                to={paths.dpiaHistory(dpia.id)}
                onHover={() => prefetch(dpiaHistoryQueryOptions(dpia.id))}
              >
                {t("Dynamic.Drawer.HistoryButton")}
              </Drawer.TabButton>
            </Drawer.TabButtons>
          </Stack>
        }
        topRightSlot={
          activeTab === "form" && (
            <DpiaActionMenu dpiaId={dpia.id} onDeleted={handleClose} ariaLabelKey="GridDrawer.Menu.ButtonLabel" />
          )
        }
      />
    </>
  );
};

export const DpiaActionMenu = ({
  dpiaId,
  onDeleted,
  ariaLabelKey,
}: {
  dpiaId: string;
  onDeleted: () => void;
  ariaLabelKey?: TranslationKey;
}) => {
  const { t } = useTranslation();
  const userInfo = useUserInfo();
  const [isOpen, setIsOpen] = useState(false);
  const dpiaQuery = useQuery({ ...dpiaByIdQueryOptions(dpiaId), enabled: isOpen });
  const dpiaGridViewQuery = useQuery({ ...dpiaGridViewQueryOptions(), enabled: isOpen });
  const connectedRegistrationsCount =
    dpiaGridViewQuery.data?.responses.find(dpia => dpia.id === dpiaId)?.connectedRegistrationsCount ?? 0;
  const deleteDpiaMutation = useMutation(deleteDpiaMutationOptions());
  const exportDpiaPdfMutation = useMutation(exportDpiaPdfMutationOptions());
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  return (
    <>
      {isEditDialogOpen && dpiaQuery.data && (
        <DpiaEditDialog
          onClose={() => {
            setIsEditDialogOpen(false);
            setIsOpen(false);
          }}
          dpia={dpiaQuery.data}
        />
      )}
      <ActionMenu
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        onTriggerHover={() => prefetch(dpiaByIdQueryOptions(dpiaId))}
        ariaLabelKey={ariaLabelKey}
      >
        <ActionMenuItemWithPermission
          missingPermission={dpiaQuery.data && !dpiaQuery.data.canEdit}
          onClick={() => setIsEditDialogOpen(true)}
        >
          <PencilIcon />
          {t("DpiaDrawer.Edit.Label")}
        </ActionMenuItemWithPermission>

        {dpiaQuery.data?.type === "internal" && (
          <ActionMenuItemWithPermission
            disabled={exportDpiaPdfMutation.isPending || dpiaQuery.isLoading}
            onClick={() => {
              if (!dpiaQuery.data) throw new Error("Dpia should always be defined here");
              return exportDpiaPdfMutation.mutate(
                { dpiaId },
                { onSuccess: response => downloadBlob(response, `${dpiaQuery.data.name}.pdf`) }
              );
            }}
          >
            <DownloadIcon />
            {t("DpiaDrawer.Export.Label")}
          </ActionMenuItemWithPermission>
        )}

        <ActionMenuDeleteMenuItem
          disabled={dpiaQuery.isLoading}
          missingPermission={dpiaQuery.data && userInfo.email !== dpiaQuery.data.responsible.email}
          confirmDeleteDialog={{
            header: t("DpiaDrawer.Delete.ConfirmHeader"),
            message: (
              <>
                <Translate
                  translationKey="DpiaDrawer.Delete.ConfirmMessage"
                  values={{
                    name: (
                      <Box component="span" key={dpiaQuery.data?.name} sx={{ fontWeight: "bold" }}>
                        {dpiaQuery.data?.name}
                      </Box>
                    ),
                  }}
                />
                {connectedRegistrationsCount > 0 && (
                  <Box
                    component="span"
                    sx={{
                      display: "flex",
                      gap: 1,
                      mt: 2,
                      fontSize: theme => theme.typography.pxToRem(12),
                      color: theme => theme.palette.warning.main,
                    }}
                  >
                    <Box component="span" sx={{ display: "flex", alignItems: "center", height: "1lh" }}>
                      <TriangleAlertIcon size={12} />
                    </Box>
                    <HACK_alignIconWithLabelBox>
                      {t(
                        `DpiaDrawer.Delete.LostRegistrationConnections.${connectedRegistrationsCount === 1 ? "Singular" : "Plural"}`,
                        { count: connectedRegistrationsCount }
                      )}
                    </HACK_alignIconWithLabelBox>
                  </Box>
                )}
              </>
            ),
            delete: t("DpiaDrawer.Delete.ConfirmDelete"),
          }}
          onClose={() => setIsOpen(false)}
          onDelete={() => deleteDpiaMutation.mutateAsync(dpiaId, { onSuccess: onDeleted })}
        />
      </ActionMenu>
    </>
  );
};
